import React, { useEffect, useRef, useState } from "react";
import { motion, useSpring } from "framer-motion";

/**
 * Custom magnetic cursor (desktop only).
 * - Dot follows mouse with subtle spring
 * - Scales up & changes color when hovering interactive elements
 * - Hidden on touch devices
 */
export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const xSpring = useSpring(0, { stiffness: 500, damping: 30, mass: 0.4 });
  const ySpring = useSpring(0, { stiffness: 500, damping: 30, mass: 0.4 });
  const ringX = useSpring(0, { stiffness: 180, damping: 24, mass: 0.6 });
  const ringY = useSpring(0, { stiffness: 180, damping: 24, mass: 0.6 });

  useEffect(() => {
    const isFine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    if (!isFine) return;
    setEnabled(true);
    document.body.classList.add("has-custom-cursor");

    const move = (e) => {
      xSpring.set(e.clientX);
      ySpring.set(e.clientY);
      ringX.set(e.clientX);
      ringY.set(e.clientY);
    };
    const over = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const interactive = t.closest("a, button, [data-magnet], input, textarea, [role='button'], .pill-nav__item, .btn-pill");
      setHovering(!!interactive);
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      document.body.classList.remove("has-custom-cursor");
    };
  }, [xSpring, ySpring, ringX, ringY]);

  if (!enabled) return null;

  return (
    <>
      <motion.div
        data-testid="custom-cursor-dot"
        className="fixed top-0 left-0 z-[200] pointer-events-none mix-blend-difference"
        style={{ x: xSpring, y: ySpring, translateX: "-50%", translateY: "-50%" }}
      >
        <motion.div
          animate={{
            scale: hovering ? 0.5 : 1,
            backgroundColor: hovering ? "#C5DB7B" : "#F5F2EB"
          }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="w-2 h-2 rounded-full"
        />
      </motion.div>
      <motion.div
        data-testid="custom-cursor-ring"
        className="fixed top-0 left-0 z-[199] pointer-events-none"
        style={{ x: ringX, y: ringY, translateX: "-50%", translateY: "-50%" }}
      >
        <motion.div
          animate={{
            scale: hovering ? 2.6 : 1,
            opacity: hovering ? 0.7 : 0.3,
            borderColor: hovering ? "#D4859B" : "rgba(245,242,235,0.4)"
          }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="w-9 h-9 rounded-full border"
        />
      </motion.div>
    </>
  );
}
